/**
 * The module definition for the RegistrationCtrl.
 */
import angular from "angular";
import _ from "lodash";

export default function RegistrationCtrl(
    $log,
    $translate,
    $scope,
    $sbAuth,
    $sbPasswordPolicy,
    $sbLocale,
    $state,
    $stateParams,
    AVAILABLE_LANGUAGE_KEYS,
    AVAILABLE_LANGUAGE_CONFIG,
    Analytics,
    $sce
) {
    "ngInject";

    var vm = this;

    vm.AVAILABLE_LANGUAGE_KEYS = AVAILABLE_LANGUAGE_KEYS;
    vm.AVAILABLE_LANGUAGE_CONFIG = AVAILABLE_LANGUAGE_CONFIG;
    vm.AVAILABLE_ORIGINS = {
        general_register: "general_register",
        free_invite_register: "free_invite_register",
        paid_invite_register: "paid_invite_register",

        track_register: "track_register",
        trace_register: "trace_register",
        flow_register: "flow_register",
        track_trial_invite_register: "track_trial_invite_register",
        trace_trial_invite_register: "trace_trial_invite_register",
        flow_trial_invite_register: "flow_trial_invite_register",
    };

    vm.jobTitles = [
        { key: "Construction Operative", name: "CONSTRUCTION_OPERATIVE" },
        {
            key: "Sub-Contractor Manager (Black Hat)",
            name: "SUB_CONTRACTOR_MANAGER",
        },
        { key: "Construction Manager", name: "CONSTRUCTION_MANAGER" },
        { key: "Construction Director", name: "CONSTRUCTION_DIRECTOR" },
        {
            key: "Project / Package Manager",
            name: "PROJECT_OR_PACKAGE_MANAGER",
        },
        { key: "Commercial Director", name: "COMMERCIAL_DIRECTOR" },
        { key: "Commercial Manager", name: "COMMERCIAL_MANAGER" },
        { key: "Planner", name: "PLANNER" },
        { key: "Project Director", name: "PROJECT_DIRECTOR" },
        { key: "Board Director", name: "BOARD_DIRECTOR" },
        {
            key: "Central Business / Offsite Management",
            name: "CENTRAL_BUSINESS_OR_OFFSITE_MANAGEMENT",
        },
        { key: "Third party Inspector", name: "THIRD_PARTY_INSPECTOR" },
        { key: "Other", name: "OTHER" },
    ];

    vm.errors = [
        {
            code: "SIGNUP_GENERIC_ERROR",
            title: "SIGNUP_GENERIC_ERROR_TITLE",
            message: "SIGNUP_GENERIC_ERROR_TEXT",
        },
        {
            code: "BlockedEmailDomainException",
            title: "SIGNUP_ERROR_EMAIL_DOMAIN_BLOCKED_TITLE",
            message: "SIGNUP_ERROR_EMAIL_DOMAIN_BLOCKED_TEXT",
            button: "SIGNUP_ERROR_EMAIL_DOMAIN_BLOCKED_BUTTON", // try again with work email,
            buttonCallback: showRegistration,
        },
        {
            code: "UsernameExistsException",
            title: "SIGNUP_ERROR_EMAIL_ALREADY_USED_TITLE",
            message: "SIGNUP_ERROR_EMAIL_ALREADY_USED_TEXT",
            button: "SIGNUP_ERROR_EMAIL_ALREADY_USED_BUTTON", // try again with different email,
            buttonCallback: showRegistration,
        },
    ];

    vm.user = {
        firstName: "",
        lastName: "",
        email: "",
        company: "",
        password: "",
        language: $sbLocale.current(),
        hasAcceptedEula: true,
        hasAcceptedDataPrivacy: true,
        hasSignedUp: false,
        signupOrigin: "",
        signupAttempts: 0,
        inProgress: false,
        hasError: false,
        errorIndex: 0,
        jobTitle: "",
        otherJobTitle: "",
        marketingConsent: "",
        marketingMaterial: true,
    };

    vm.register = register;
    vm.tryAgain = tryAgain;
    vm.triggerErrorAction = triggerErrorAction;
    vm.isFreeUser = isFreeUser;
    vm.nextClicked = _nextClicked;
    vm._prefillNamesFromEmail = _prefillNamesFromEmail;

    vm.urlExtension = $translate.use() === "de" ? "" : "en/";

    // Maximum attempts before suggesting the user to contact support.
    vm.MAX_SIGNUP_ATTEMPTS = 2;

    vm.passwordPolicy = "";
    vm.visibleStepNumber = 0;
    vm.totalStepNumber = 2;

    /////////////////////
    //
    //      WATCHER
    //
    /////////////////////

    // Watch change password events and trigger the validation
    $scope.$watch(
        "$ctrl.userRegistrationForm.newPassword.$$rawModelValue",
        _passwordWatcher
    );
    $scope.$on("$destroy", function () {
        _passwordWatcher();
    });

    /////////////////////
    //
    //      IMPL
    //
    /////////////////////

    _activate();

    function _activate() {
        _setSignupOrigin();
        _setMarketingConsent();
        return _getPasswordPolicy();
    }

    function _setMarketingConsent() {
        /**
         * AWS Cognito custom fields only accepts string or number and
         * therefore the marketing consent boolean is mapped to either
         * a "true" or "false" string.
         * (those are also the configured strings in hubspot)
         */
        const signupOriginParam = $stateParams.signup_origin;

        if (
            signupOriginParam !== vm.AVAILABLE_ORIGINS.general_register &&
            signupOriginParam !== vm.AVAILABLE_ORIGINS.paid_invite_register
        ) {
            vm.user.marketingConsent = "true";
        }
    }

    function _getPasswordPolicy() {
        return $sbPasswordPolicy.getPolicy().then(_setPolicy);
    }

    function _setPolicy(passwordPolicy) {
        vm.passwordPolicy = passwordPolicy;
    }

    function hasPolicy() {
        return angular.isObject(vm.passwordPolicy);
    }

    function _passwordWatcher(newPassword) {
        if (!angular.isString(newPassword) || newPassword.length === 0) {
            return;
        }

        vm.passwordPatternError = "";

        if (!hasPolicy()) {
            return;
        }

        $sbPasswordPolicy
            .getPasswordErrorTranslated(newPassword, vm.passwordPolicy)
            .then(function (translatedError) {
                vm.passwordPatternError = translatedError.join(", ");
            });
    }

    function _setSignupOrigin() {
        const signupOriginParam = $stateParams.signup_origin;
        if (vm.AVAILABLE_ORIGINS[signupOriginParam]) {
            vm.user.signupOrigin = vm.AVAILABLE_ORIGINS[signupOriginParam];
            Analytics.trackConversion(
                "registration page opened",
                vm.user.signupOrigin
            );
        } else {
            $state.go($state.$current.name, {
                signup_origin: vm.AVAILABLE_ORIGINS.general_register,
            });
        }
    }

    function _prefillNamesFromEmail() {
        const capitalize = (str) => {
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        const localPart = vm.user.email.split("@")[0];
        const delimiters = ["_", ".", "-"];

        let localParts = [localPart];

        for (const delimiter of delimiters) {
            if (localPart.includes(delimiter)) {
                localParts = localPart.split(delimiter);
                break;
            }
        }

        localParts = localParts
            .map((part) => part.replace(/\d+/g, "").replace(/\+.*$/, "").trim())
            .filter(Boolean);

        if (localParts.length >= 2) {
            vm.user.firstName = capitalize(localParts[0]);
            vm.user.lastName = capitalize(localParts[1]);
        } else {
            vm.user.firstName = capitalize(localParts[0]);
        }
    }

    /**
     * Called to submit the form. The user contains the input values from the user.
     * The data is already pre-validated by the forms html.
     *
     * @param {user} user - User information from form input.
     */
    function register(user) {
        const derivedCompany = deriveCompany(user.email);

        user.company = derivedCompany;
        user.signupAttempts += 1;
        user.inProgress = true;

        Analytics.trackConversion("registration clicked", vm.user.signupOrigin);

        $sbAuth
            .signUp(user)
            .then(_showSuccessAndWait)
            .catch(function (error) {
                try {
                    console.error(
                        "Error during signup:",
                        JSON.stringify(
                            error,
                            Object.getOwnPropertyNames(error),
                            2
                        )
                    );
                    setErrorIndexBy(error.code);
                    _showError();
                } catch (e) {
                    $log.error(e);
                    _showFirstStepErrors();
                }
            })
            .finally(function () {
                user.inProgress = false;
            });
    }

    function setErrorIndexBy(errorCode) {
        vm.user.errorIndex = _.findIndex(vm.errors, function (error) {
            return error.code === errorCode;
        });
        if (!angular.isNumber(vm.user.errorIndex) || vm.user.errorIndex < 0) {
            vm.user.errorIndex = 0;
        }
    }

    function tryAgain(user) {
        user.signupAttempts += 1;
        if (user.signupAttempts > vm.MAX_SIGNUP_ATTEMPTS) {
            user.hasError = true;
        } else {
            register(user);
        }
    }

    function triggerErrorAction(index) {
        if (vm.errors[index].buttonCallback) {
            vm.errors[index].buttonCallback();
        }
    }

    /**
     * Remove email, but keep other data when showing the registration form again.
     * The user can start from the beginning, but keeps his other input in following steps of the form.
     */
    function showRegistration() {
        vm.visibleStepNumber = 0;
        vm.user.email = "";
        vm.user.hasError = false;
    }

    function isFreeUser() {
        // according to https://sablono.atlassian.net/browse/SAB-7030 (Brian)
        // It is not wanted that users on the "paid" plan have to consent to marketing
        //  to avoid targeting them with marketing material
        //
        // so, we only show the consent if the user is on a free plan
        return (
            [
                vm.AVAILABLE_ORIGINS.free_invite_register,
                vm.AVAILABLE_ORIGINS.track_register,
                vm.AVAILABLE_ORIGINS.trace_register,
                vm.AVAILABLE_ORIGINS.flow_register,
                vm.AVAILABLE_ORIGINS.track_trial_invite_register,
                vm.AVAILABLE_ORIGINS.trace_trial_invite_register,
                vm.AVAILABLE_ORIGINS.flow_trial_invite_register,
            ].indexOf(vm.user.signupOrigin) > -1
        );
    }

    /**
     * Function for error and success handling
     */
    function _showSuccessAndWait() {
        vm.user.hasSignedUp = true;
        Analytics.trackEvent("sign_up", "submit", "registration_form");
    }

    function _showFirstStepErrors() {
        vm.user.hasError = true;
        vm.user.errorIndex = 1;
        if (vm.user.signupAttempts >= vm.MAX_SIGNUP_ATTEMPTS) {
            _showGenericError();
        }
    }

    function _showError() {
        vm.user.hasError = true;
    }

    function _showGenericError() {
        vm.user.hasError = true;
        vm.user.errorIndex = 0;
    }

    function _nextClicked() {
        vm.visibleStepNumber += 1;
        switch (vm.visibleStepNumber) {
            case 1:
                vm._prefillNamesFromEmail();
                Analytics.trackConversion(
                    "email, password, legal provided",
                    vm.user.signupOrigin
                );
                break;
            case 2:
                Analytics.trackConversion(
                    "personal info provided",
                    vm.user.signupOrigin
                );
                break;
        }
    }

    function deriveCompany(email) {
        const mailProvider = email.split("@")[1];
        if (!mailProvider) {
            return "";
        }

        const splitProvider = mailProvider.split(".");

        // use the first part of the (sub)domain of the second-level domain
        //  -> https://sablono.atlassian.net/browse/SAB-13124
        //
        // examples:
        // bar.com is a regular second-level domain                 -> bar
        // foo.bar.com is just the “foo” subdomain of bar domain    -> foo
        // bar.co.uk is a second-level domain under the .co.uk      -> bar
        // foo.bar.co.uk is the “foo” subdomain of bar domain       -> foo
        return splitProvider[0];
    }
}
